import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Chip, Paper, Skeleton, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, TextField,
  InputAdornment, IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  RocketLaunch as ActiveIcon,
  Archive as DeprecatedIcon,
  Edit as DraftIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toServerDate } from '../../timezone';

/**
 * Cross-tenant workbook monitoring for the operator. Single page with one
 * row per (tenant × workbook) showing:
 *
 * <ul>
 *   <li>Which tenant owns it</li>
 *   <li>Lifecycle status (draft / active / deprecated)</li>
 *   <li>Schedule (cron + enabled chip)</li>
 *   <li>Health: success rate + last-run status from the scorecard service</li>
 *   <li>Total runs + avg duration</li>
 * </ul>
 *
 * <p>Powered by {@code GET /api/operator/workbooks} which iterates every
 * org and pulls its scorecards — so the operator gets a fleet view without
 * needing to switch tenant context manually.
 */

const fmtDuration = (ms) => {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000)        return `${Math.round(ms)}ms`;
  if (ms < 60_000)      return `${(ms / 1000).toFixed(1)}s`;
  if (ms < 3_600_000)   return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
  return `${Math.floor(ms / 3_600_000)}h ${Math.floor((ms % 3_600_000) / 60_000)}m`;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const lifecycleChip = (status) => {
  const map = {
    active:     { color: 'success', icon: <ActiveIcon fontSize="inherit" /> },
    deprecated: { color: 'default', icon: <DeprecatedIcon fontSize="inherit" /> },
    draft:      { color: 'primary', icon: <DraftIcon fontSize="inherit" /> },
  };
  const cfg = map[status] || map.draft;
  return (
    <Chip size="small" icon={cfg.icon} label={status || 'draft'} color={cfg.color}
          variant={status === 'draft' ? 'outlined' : 'filled'}
          sx={{ textTransform: 'capitalize' }} />
  );
};

const healthChip = (sc) => {
  if (!sc || !sc.totalRuns) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  const pct = sc.successRate == null ? null : Math.round(sc.successRate);
  const color = pct == null ? 'default' : pct >= 95 ? 'success' : pct >= 80 ? 'warning' : 'error';
  const last = sc.lastStatus;
  const lastColor = last === 'completed' ? 'success' : last === 'failed' ? 'error'
                 : last === 'running' ? 'info' : 'default';
  return (
    <Stack direction="row" spacing={0.5}>
      {pct != null && (
        <Chip size="small" label={`${pct}%`} color={color}
              variant={pct >= 95 ? 'filled' : 'outlined'} />
      )}
      {last && (
        <Chip size="small" label={last} color={lastColor} variant="outlined"
              sx={{ textTransform: 'capitalize' }} />
      )}
    </Stack>
  );
};

export default function OperatorWorkbooksPage() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState(null);
  const [q, setQ]           = useState('');

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get('/api/operator/workbooks', { headers: getAuthHeaders() });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(needle) ||
      (r.orgSlug || '').toLowerCase().includes(needle) ||
      (r.orgName || '').toLowerCase().includes(needle)
    );
  }, [rows, q]);

  const totals = useMemo(() => ({
    orgs:     new Set(rows.map((r) => r.orgId)).size,
    books:    rows.length,
    active:   rows.filter((r) => r.status === 'active').length,
    scheduled: rows.filter((r) => r.scheduleEnabled).length,
  }), [rows]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Workbooks — all tenants</Typography>
        <Typography variant="caption" color="text.secondary">
          {totals.orgs} orgs · {totals.books} workbooks · {totals.active} active · {totals.scheduled} scheduled
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Stack>

      <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
        <TextField
          fullWidth size="small" placeholder="Search by workbook name or tenant slug…"
          value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: (
            <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
        />
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Workbook</TableCell>
              <TableCell>Lifecycle</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Health</TableCell>
              <TableCell align="right">Runs</TableCell>
              <TableCell align="right">Avg duration</TableCell>
              <TableCell>Last run</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [0,1,2,3].map((i) => (
                <TableRow key={i}><TableCell colSpan={8}><Skeleton height={32} /></TableCell></TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No workbooks match your search.
                </Typography>
              </TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={`${r.orgId}-${r.workflowId}`} hover>
                <TableCell>
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>{r.orgName}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.orgSlug}</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack>
                    <Typography variant="body2" fontWeight={500}>{r.name}</Typography>
                    {r.description && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 320 }}>
                        {r.description}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{lifecycleChip(r.status)}</TableCell>
                <TableCell>
                  {r.scheduleCron ? (
                    <Tooltip title={r.scheduleCron}>
                      <Chip size="small" icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                            label={r.scheduleEnabled ? 'on' : 'paused'}
                            color={r.scheduleEnabled ? 'info' : 'default'}
                            variant={r.scheduleEnabled ? 'filled' : 'outlined'} />
                    </Tooltip>
                  ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                </TableCell>
                <TableCell>{healthChip(r.scorecard)}</TableCell>
                <TableCell align="right">{r.scorecard?.totalRuns ?? 0}</TableCell>
                <TableCell align="right">{fmtDuration(r.scorecard?.avgDurationMs)}</TableCell>
                <TableCell>
                  {toServerDate(r.scorecard?.lastStartedAt)?.toLocaleString() ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
